import { useShallow } from 'zustand/react/shallow'
import { useOfferStore, selectComparisonGroups, selectAHCatalogProducts } from '@store/offerStore'
import { SUPERMARKETS, formatPrice } from '@utils/constants'
import { findCatalogMatch } from '@utils/premiumMatcher'
import { SupermarketBadge } from './SupermarketBadge'
import { EmptyState } from './EmptyState'
import styles from './ComparisonView.module.css'

/** @import { ComparisonGroup, CatalogMatch, Offer } from '../types.js' */

function ComparisonItem({ offer, isCheapest, barWidth, layoutClassName = undefined }) {
  const itemClass = isCheapest
    ? `${styles.componentComparisonItem} ${styles.itemCheapest}`
    : styles.componentComparisonItem

  const barBackground = isCheapest
    ? 'var(--color-text)'
    : 'var(--color-text-muted)'

  const label = SUPERMARKETS[offer.supermarket]?.label || offer.supermarket

  return (
    <div className={`${itemClass} ${layoutClassName || ''}`}>
      <SupermarketBadge supermarket={offer.supermarket} size="xs" />
      <span className={styles.itemSupermarket}>{label}</span>
      <span className={styles.itemPrice}>{formatPrice(offer.offerPrice)}</span>
      <span className={styles.itemUnit}>{offer.unit}</span>
      <div className={styles.bar}>
        <div
          className={styles.barFill}
          style={{ width: `${barWidth}%`, background: barBackground }}
        />
      </div>
    </div>
  )
}

function PremiumComparisonItem({ premiumMatch, isCheapest, barWidth, layoutClassName = undefined }) {
  const itemClass = [
    styles.componentComparisonItem,
    styles.itemPremium,
    isCheapest ? styles.itemCheapest : '',
  ].filter(Boolean).join(' ')

  const barBackground = isCheapest
    ? 'var(--color-text)'
    : 'var(--color-text-muted)'

  return (
    <div className={`${itemClass} ${layoutClassName || ''}`}>
      <SupermarketBadge supermarket="ah" size="xs" />
      <span className={styles.itemSupermarket}>AH Premium 💎</span>
      <span className={styles.itemPrice}>{formatPrice(premiumMatch.premiumPrice)}</span>
      <span className={styles.itemUnit}>{premiumMatch.unit}</span>
      <div className={styles.bar}>
        <div
          className={styles.barFill}
          style={{ width: `${barWidth}%`, background: barBackground }}
        />
      </div>
      <span className={styles.premiumEveryWeek}>elke week</span>
    </div>
  )
}

function findPremiumEntry(group, catalogProducts, hasAHPremium) {
  if (!hasAHPremium || !catalogProducts) return null

  const category = group.offers[0]?.category
  const match = findCatalogMatch(catalogProducts, group.label, category)
    ?? findCatalogMatch(catalogProducts, group.offers[0]?.productName, category)

  if (!match) return null

  const hasAHOffer = group.offers.some(o => o.supermarket === 'ah')
  if (hasAHOffer) return null

  return match
}

function buildSortedEntries(group, premiumMatch) {
  const entries = group.offers.map(o => ({ type: 'offer', offer: o }))

  if (premiumMatch) {
    entries.push({
      type: 'premium',
      offer: { offerPrice: premiumMatch.premiumPrice, supermarket: 'ah', unit: premiumMatch.unit },
    })
  }

  return entries.sort((a, b) => a.offer.offerPrice - b.offer.offerPrice)
}

function ComparisonGroup({ group, catalogProducts, hasAHPremium }) {
  const premiumMatch = findPremiumEntry(group, catalogProducts, hasAHPremium)
  const sorted = buildSortedEntries(group, premiumMatch)

  const cheapestPrice = sorted[0]?.offer.offerPrice
  const maxPrice = Math.max(...sorted.map(e => e.offer.offerPrice))
  const categoryEmoji = group.offers[0]?.category === 'fruit' ? '🍎' : '🥦'

  return (
    <div className={styles.componentComparisonGroup}>
      <h3 className={styles.groupTitle}>
        <span>{categoryEmoji}</span>
        {group.label}
      </h3>

      <div className={styles.items}>
        {sorted.map((entry, i) => {
          const isCheapest = entry.offer.offerPrice === cheapestPrice && sorted.length > 1
          const barWidth = maxPrice > 0
            ? Math.round((entry.offer.offerPrice / maxPrice) * 100)
            : 100

          if (entry.type === 'premium') {
            return (
              <PremiumComparisonItem
                key="ah-premium"
                {...{ premiumMatch, isCheapest, barWidth }}
                layoutClassName={styles.comparisonItemLayout}
              />
            )
          }

          return (
            <ComparisonItem
              key={entry.offer.id ?? `offer-${i}`}
              offer={entry.offer}
              {...{ isCheapest, barWidth }}
              layoutClassName={styles.comparisonItemLayout}
            />
          )
        })}
      </div>
    </div>
  )
}

export function ComparisonView({ layoutClassName = undefined }) {
  const groups = useOfferStore(useShallow(selectComparisonGroups))
  const hasAHPremium = useOfferStore(s => s.hasAHPremium)
  const catalogProducts = useOfferStore(selectAHCatalogProducts)

  if (groups.length === 0) {
    return (
      <EmptyState
        type="no-comparison"
      />
    )
  }

  return (
    <div className={`${styles.component} ${layoutClassName || ''}`}>
      {groups.map((group) => (
        <ComparisonGroup key={group.key} {...{ group, catalogProducts, hasAHPremium }} />
      ))}
    </div>
  )
}
